import React, {useMemo, useState} from 'react';
import {Box, Backdrop, CircularProgress, FormControl, InputLabel, Select, MenuItem, Typography} from '@mui/material';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import FiltersTab from '../DownloadReportPopup/FiltersTab';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';
import {useGetApplicationStatusesQuery} from '../../../../api/services/schoolManagementService';
import {useBulkStatusUpdateMutation} from '../../../../api/services/profilesService';
import useNotification from '../../../../utility/hooks/useNotification';

/* eslint-disable react/prop-types */
const BulkStatusUpdatePopup = ({open, onClose, groupFilters}) => {
    const [loading, setLoading] = useState(false);
    const [strictRecipientsFiltering, setStrictRecipientsFiltering] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [settings, setSettings] = useState({
        groupFilters: [],
        studentFilters: [],
        applicationFilters: [],
        enquiryFilters: []
    });

    const {data: applicationStatuses = []} = useGetApplicationStatusesQuery();
    const {showSuccessNotification, showErrorNotification} = useNotification();

    const filteredGroupFilterModules = useMemo(() => groupFilters
            ?.filter((group) =>
                group.name?.toLowerCase().includes('pre-admission') ||
                group.name?.toLowerCase().includes('school group') ||
                group.name?.toLowerCase().includes('my group'))
            .map(group => {
                if (group.name?.toLowerCase().includes('school group') || group.name?.toLowerCase().includes('my group')) {
                    return {
                        ...group,
                        filters: group.filters.filter(filter => filter.membersType !== GROUP_MEMBERS_TYPE.employee)
                    };
                }
                return group;
            }) || [],
        [groupFilters?.length]);

    const [bulkStatusUpdate] = useBulkStatusUpdateMutation();

    const handleUpdateStatus = async () => {
        if (!selectedStatus) {
            showErrorNotification('Please select an application status');
            return;
        }

        const hasSelectedItems = settings.applicationFilters.length > 0 ||
            settings.studentFilters.length > 0 ||
            settings.enquiryFilters.length > 0 ||
            settings.groupFilters.filter(e => !e.excluded).length > 0;

        if (!hasSelectedItems) {
            showErrorNotification('Please select at least one group, student, application, or enquiry');
            return;
        }

        setLoading(true);
        try {
            await bulkStatusUpdate({
                status: selectedStatus,
                filterIds: settings.groupFilters.filter(e => !e.excluded).map(e => e.id),
                excludedFilterIds: settings.groupFilters.filter(e => e.excluded).map(e => e.id),
                strictRecipientsFiltering,
                studentIds: [
                    ...settings.studentFilters.map(e => e.id),
                    ...settings.applicationFilters.map(e => e.id),
                    ...settings.enquiryFilters.map(e => e.id)
                ]
            }).unwrap();

            showSuccessNotification('Application status updated successfully');
            handleClose();
        } catch (error) {
            showErrorNotification('Failed to update application status');
            console.error('Bulk status update error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedStatus('');
        setSettings({
            groupFilters: [],
            studentFilters: [],
            applicationFilters: [],
            enquiryFilters: []
        });
        onClose();
    };

    const hasSelectedItems = settings.applicationFilters.length > 0 ||
        settings.groupFilters.filter(e => !e.excluded).length > 0;

    return (
        <>
            <AdmicityDialog
                open={open}
                handleClose={handleClose}
                title={'Bulk Application Status Update'}
                maxWidth={'md'}
                actions={[
                    {
                        label: 'Cancel',
                        onClick: handleClose,
                    },
                    {
                        label: 'Update Status',
                        onClick: handleUpdateStatus,
                        disabled: !selectedStatus || !hasSelectedItems
                    }
                ]}
            >
                <Box sx={{minHeight: '420px', p: 2}}>
                    {/* Filters Section */}
                    <Box sx={{mb: 3}}>
                        <Typography variant="h6" sx={{mb: 2, fontWeight: 600}}>
                            Select Filters
                        </Typography>
                        <FiltersTab
                            data={filteredGroupFilterModules}
                            settings={settings}
                            strictRecipientsFiltering={strictRecipientsFiltering}
                            setStrictRecipientsFiltering={setStrictRecipientsFiltering}
                            setSettings={setSettings}
                            disableEnquirySelect={true}
                            hideStudentSelect={true}
                        />
                    </Box>

                    <Box sx={{mt: 4, pt: 3, borderTop: '1px solid #e0e0e0'}}>
                        <Typography variant="h6" sx={{mb: 2, fontWeight: 600}}>
                            Select Application Status
                        </Typography>
                        <FormControl fullWidth required>
                            <InputLabel>Application Status</InputLabel>
                            <Select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                label="Application Status"
                            >
                                {applicationStatuses.map((status) => (
                                    <MenuItem key={status.id} value={status.id}>
                                        {status.description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            </AdmicityDialog>
            <Backdrop
                open={loading}
                sx={{
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    colour: '#fff',
                    flexDirection: 'column'
                }}
            >
                <CircularProgress/>
            </Backdrop>
        </>
    );
};

export default BulkStatusUpdatePopup;